/**
 * CheepSweepz storefront: landed prices are quoted in USD with official BBD peg display.
 * BBD = landed USD × 2 (Barbados fixed peg). Duty/margin/freight are already in landed USD.
 */
import { landedBbdFromUsdLanded, USD_BBD_OFFICIAL_PEG } from './barbados-vat.js';

export { USD_BBD_OFFICIAL_PEG, landedBbdFromUsdLanded };

/** Normalize a landed USD amount for display. */
export function normalizeUsdLanded(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100) / 100;
}

/** USD + BBD pair from landed USD. */
export function usdBbdFromLandedUsd(landedUsd) {
  const usd = normalizeUsdLanded(landedUsd);
  const bbd = landedBbdFromUsdLanded(usd);
  return { usd, bbd };
}

/** Single-line label: US$29.49 | BBD $58.98 */
export function formatUsdBbdDualLine(landedUsd) {
  const { usd, bbd } = usdBbdFromLandedUsd(landedUsd);
  if (usd <= 0) return null;
  return `US$${usd.toFixed(2)} | BBD $${bbd.toFixed(2)}`;
}

/** Sort variants / products ascending by source cost (smallest pack → largest). */
export function sortBySourceCostAsc(items, costKey = 'extracted_cost_price') {
  if (!Array.isArray(items)) return [];
  return [...items].sort(
    (a, b) => (Number(a?.[costKey]) || 0) - (Number(b?.[costKey]) || 0),
  );
}

/** Sort catalog rows by landed price ascending. */
export function sortByLandedPriceAsc(items) {
  if (!Array.isArray(items)) return [];
  return [...items].sort(
    (a, b) =>
      (Number(a?.landed_price ?? a?.calculated_landed_price) || 0) -
      (Number(b?.landed_price ?? b?.calculated_landed_price) || 0),
  );
}

/** True when landed price scales up with source cost (matrix monotonicity check). */
export function isMonotonicLandedPricing(variants) {
  const sorted = sortBySourceCostAsc(variants);
  let prevLanded = 0;
  for (const v of sorted) {
    const landed = Number(v?.calculated_landed_price ?? v?.landed_price) || 0;
    const cost = Number(v?.extracted_cost_price) || 0;
    if (cost <= 0 || landed <= 0) continue;
    if (prevLanded > 0 && landed < prevLanded) return false;
    prevLanded = landed;
  }
  return true;
}
